import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bus, Calendar, Briefcase, MapPin, CheckCircle, Star } from 'lucide-react';
import { services, stats, testimonials, companyInfo } from '../data/mock';

export const Home = () => {
  const iconMap = {
    Bus: Bus,
    Calendar: Calendar,
    Briefcase: Briefcase,
    MapPin: MapPin
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2b874a2f-960a-4738-be43-cfe85cac4e30/artifacts/urzcrznv_IMG_0605%20copy.JPEG)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>

          <div className="absolute inset-0 bg-gradient-to-r from-[#003d82]/95 to-[#003d82]/70" />
        </div>

        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-2xl text-white">
            <h1 className="mb-6 leading-tight">
              <span className="block text-white text-6xl md:text-8xl font-black tracking-tighter mb-6 drop-shadow-2xl" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-0.04em', textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                Litoral Norte
              </span>
              <span className="block text-[#f5a623] text-2xl md:text-3xl font-semibold tracking-wide drop-shadow-lg" style={{ fontFamily: "'Inter', sans-serif" }}>
                Transportes Rodoviários de Passageiros
              </span>
            </h1>
            <p className="text-xl mb-8 text-gray-200">Há mais de 20 anos a servir a comunidade da Póvoa de Varzim e Vila do Conde com excelência, confiança e profissionalismo.

            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contacto"
                className="bg-[#f5a623] text-[#003d82] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#e5a71b] transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-2">

                Pedir Orçamento
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/servicos"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/30">

                Conheça os Serviços
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div
              key={index}
              className="text-center p-6 rounded-xl bg-gradient-to-br from-[#003d82]/5 to-transparent hover:shadow-lg transition-all duration-300">

                <div className="text-4xl md:text-5xl font-bold text-[#003d82] mb-2">
                  {stat.number}{stat.suffix}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003d82] mb-4">Os Nossos Serviços</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Soluções completas de transporte para todas as suas necessidades
            </p>
          </div>

          <div className="overflow-x-auto pb-4">
            <div className="flex gap-8 min-w-max px-4">
              {services.map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <div
                    key={service.id}
                    className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 w-80 flex-shrink-0">

                    <div className="w-16 h-16 rounded-lg bg-[#f5a623]/10 flex items-center justify-center mb-6">
                      <Icon size={32} className="text-[#003d82]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#003d82] mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) =>
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle size={16} className="text-[#f5a623] flex-shrink-0" />
                          {feature}
                        </li>
                      )}
                    </ul>
                  </div>);

              })}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-[#003d82] font-semibold hover:text-[#f5a623] transition-colors">

              Ver todos os serviços
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#003d82] mb-6">
                Porquê Escolher a Litoral Norte?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Desde {new Date(companyInfo.founded).getFullYear()}, somos referência no transporte rodoviário de passageiros na região. Com uma frota moderna de {companyInfo.fleetSize} autocarros e uma equipa dedicada, garantimos segurança, conforto e profissionalismo em todos os serviços.
              
              </p>
              <div className="space-y-4">
                {["Frota moderna",

                'Motoristas experientes e certificados',
                'Pontualidade e fiabilidade',
                'Preços competitivos',
                'Atendimento personalizado',
                'Mais de 20 anos de experiência'].
                map((item, index) =>
                <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#f5a623] flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={16} className="text-[#003d82]" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                )}
              </div>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 mt-8 text-[#003d82] font-semibold hover:text-[#f5a623] transition-colors">

                Saiba mais sobre nós
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_povoa-transport/artifacts/2135g1ad_WhatsApp%20Image%202026-04-08%20at%2014.12.24.jpeg"
                alt="Autocarro Litoral Norte"
                className="rounded-2xl shadow-2xl" />

              <div className="absolute -bottom-6 -left-6 bg-[#f5a623] text-[#003d82] p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">{companyInfo.fleetSize}</div>
                <div className="font-semibold">Autocarros</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003d82] mb-4">O Que Dizem os Nossos Clientes</h2>
            <p className="text-xl text-gray-600">A satisfação dos nossos clientes é a nossa prioridade</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial) =>
            <div
              key={testimonial.id}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300">

                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) =>
                <Star key={i} size={18} className="fill-[#f5a623] text-[#f5a623]" />
                )}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-[#003d82]">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.company}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003d82] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Pronto Para Viajar Connosco?</h2>
            <p className="text-xl mb-8 text-gray-200">Entre em contacto connosco para obter um orçamento personalizado ou esclarecer qualquer dúvida. A nossa equipa está pronta para ajudar!


            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contacto"
                className="bg-[#f5a623] text-[#003d82] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#e5a71b] transition-all duration-300 hover:shadow-xl hover:scale-105">

                Pedir Orçamento Gratuito
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/30">

                Ligar Agora
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};